import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "hrm-solution" },
      { name: "LMS Solution", link: "lms-solution" },
      { name: "POS Solution", link: "pos-solution" },
      { name: "SaaS Platform", link: "saas-solution" },
    ],
  },
  {
    label: "Industries",
   dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "/retail-ecommerce-solution" },
      { name: "Real Estate", link: "realestate-solution" },
      { name: "Logistics", link: "logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🏥",
    title: "Hospital Management System",
    desc: "End-to-end HMS covering OPD/IPD workflows, bed management, staff scheduling, pharmacy, lab, billing, and real-time dashboards — built for 50-bed clinics to 2,000-bed tertiary hospitals.",
    tags: ["OPD/IPD", "HL7 FHIR", "Billing", "Pharmacy"],
    color: "#0D9488",
  },
  {
    icon: "📱",
    title: "Telemedicine Platform",
    desc: "HIPAA-compliant video consultations, e-prescriptions, asynchronous messaging, appointment booking, and specialist referrals — connecting patients and doctors across any device, anywhere.",
    tags: ["WebRTC", "HIPAA", "E-Prescriptions", "iOS & Android"],
    color: "#2563EB",
  },
  {
    icon: "📋",
    title: "Electronic Health Records (EHR)",
    desc: "Structured, interoperable EHR systems with SOAP notes, FHIR-compliant data exchange, clinical decision support, and complete audit trails — meeting NABH and JCI standards.",
    tags: ["HL7 FHIR", "SNOMED CT", "NABH", "ICD-10"],
    color: "#7C3AED",
  },
  {
    icon: "🤖",
    title: "AI-Powered Diagnostics",
    desc: "Computer vision for radiology (X-ray, CT, MRI), NLP clinical note extraction, predictive risk scoring, drug interaction alerts, and AI triage assistants with 94%+ accuracy.",
    tags: ["Computer Vision", "NLP", "GPT-4", "DICOM"],
    color: "#DC2626",
  },
  {
    icon: "💊",
    title: "Pharmacy & Inventory Management",
    desc: "Real-time drug inventory, automated reorder triggers, expiry tracking, narcotic register compliance, supplier management, and integration with HMS billing modules.",
    tags: ["Barcode Scanning", "Auto-Reorder", "GST Billing", "Narcotics"],
    color: "#D97706",
  },
  {
    icon: "🧬",
    title: "Laboratory Information System (LIS)",
    desc: "Sample collection to report delivery — barcode tracking, instrument interfaces, auto-validation, TAT monitoring, and patient portal with downloadable reports and WhatsApp delivery.",
    tags: ["ASTM", "Barcode", "Auto-validation", "Patient Portal"],
    color: "#059669",
  },
  {
    icon: "📊",
    title: "Patient Engagement & RPM",
    desc: "Remote patient monitoring with IoT device integration, chronic disease management programs, medication adherence tracking, push alerts, and health coaching chat.",
    tags: ["IoT Wearables", "Bluetooth", "Push Alerts", "Dashboards"],
    color: "#0891B2",
  },
  {
    icon: "🔐",
    title: "Healthcare Data Security & Compliance",
    desc: "End-to-end encryption, role-based access control, HIPAA risk assessments, DISHA compliance (India), audit log management, and penetration testing for healthcare environments.",
    tags: ["HIPAA", "DISHA", "Encryption", "Pen Testing"],
    color: "#4F46E5",
  },
];

const TECH_STACK = [
  {
    category: "Frontend",
    items: [
      { name: "React.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000000" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg", color: "#02569B" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Angular", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angularjs/angularjs-original.svg", color: "#DD0031" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
    ],
  },
  {
    category: "Backend & AI",
    items: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "Django", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/django/django-plain.svg", color: "#092E20" },
      { name: "TensorFlow", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tensorflow/tensorflow-original.svg", color: "#FF6F00" },
      { name: "PyTorch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/pytorch/pytorch-original.svg", color: "#EE4C2C" },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/graphql/graphql-plain.svg", color: "#E10098" },
    ],
  },
  {
    category: "Database & Storage",
    items: [
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg", color: "#4479A1" },
      { name: "Firebase", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/firebase/firebase-plain.svg", color: "#FFCA28" },
      { name: "Elasticsearch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/elasticsearch/elasticsearch-original.svg", color: "#005571" },
    ],
  },
  {
    category: "Cloud & Compliance",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "Azure", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
      { name: "Terraform", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/terraform/terraform-original.svg", color: "#7B42BC" },
      { name: "GitHub Actions", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg", color: "#181717" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Clinical Discovery & Compliance Mapping", desc: "Stakeholder interviews with clinicians, IT leads, and administrators — mapping workflows, data flows, regulatory requirements (HIPAA, DISHA, NABH), and integration touchpoints before any design begins.", color: "#0D9488" },
  { n: "02", icon: "🎨", title: "UX Research & Clinical Design", desc: "User research with real clinical staff — doctors, nurses, lab technicians. Low-fi prototypes validated in simulated clinical environments, then high-fidelity Figma designs optimised for high-pressure, time-critical use.", color: "#2563EB" },
  { n: "03", icon: "⚙️", title: "Agile Development with Clinical QA", desc: "2-week sprints with dedicated clinical QA — real-world scenario testing, edge case handling, and clinical workflow simulations alongside standard automated test suites.", color: "#7C3AED" },
  { n: "04", icon: "🔗", title: "Integrations & Interoperability", desc: "HL7 FHIR APIs, DICOM imaging integration, lab instrument interfaces (ASTM), insurance claim APIs, government health portals (ABDM/ABHA), and third-party payment gateways.", color: "#D97706" },
  { n: "05", icon: "🧪", title: "Validation, UAT & Go-Live", desc: "Clinical user acceptance testing with real patients and clinicians, penetration testing, HIPAA risk assessment sign-off, staff training, and a phased go-live with 24/7 on-site support.", color: "#DC2626" },
  { n: "06", icon: "🛡️", title: "Monitoring, Security & Growth", desc: "99.9% uptime SLA, real-time alerting, quarterly security audits, HIPAA audit log reviews, monthly product updates driven by clinical user feedback and outcome analytics.", color: "#059669" },
];

const CASE_STUDIES = [
  {
    title: "MediCore HMS — 1,200-Bed Hospital",
    industry: "Hospital Management",
    desc: "Built a fully integrated HMS for a 1,200-bed tertiary care hospital in Delhi — covering OPD, IPD, OT scheduling, pharmacy, lab, radiology, billing, and a patient app. Reduced administrative time by 52% and cut billing errors to near-zero.",
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop&auto=format",
    tags: ["React", "Node.js", "HL7 FHIR", "AWS"],
    metrics: [{ val: "52%", lbl: "Admin Time Saved" }, { val: "99.9%", lbl: "Uptime SLA" }, { val: "₹0", lbl: "Billing Errors" }],
    color: "#0D9488",
  },
  {
    title: "CareLink Telemedicine — 200K Patients",
    industry: "Telemedicine",
    desc: "A pan-India HIPAA-compliant telemedicine platform connecting 2,000+ doctors to 200,000+ patients — with video consultations, AI symptom checker, e-prescriptions, and insurance claim filing in one seamless app.",
    img: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600&h=400&fit=crop&auto=format",
    tags: ["Flutter", "WebRTC", "GPT-4", "Razorpay"],
    metrics: [{ val: "200K+", lbl: "Active Patients" }, { val: "2,000+", lbl: "Doctors Onboarded" }, { val: "4.8★", lbl: "Play Store" }],
    color: "#2563EB",
  },
  {
    title: "NeuroCare AI Diagnostics",
    industry: "AI / Radiology",
    desc: "An AI-powered radiology assistant analysing CT and MRI scans for 47 neurological conditions — deployed across 80 hospitals, achieving 94.6% diagnostic accuracy and reducing radiologist review time by 43%.",
    img: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&h=400&fit=crop&auto=format",
    tags: ["PyTorch", "DICOM", "AWS SageMaker", "React"],
    metrics: [{ val: "94.6%", lbl: "Diagnostic Accuracy" }, { val: "43%", lbl: "Faster Reviews" }, { val: "80+", lbl: "Hospitals" }],
    color: "#7C3AED",
  },
];

const WHY_US = [
  { icon: "🏥", title: "Deep Clinical Domain Knowledge", desc: "Our team includes ex-hospital IT leads, clinical informatics specialists, and designers who have worked inside real hospital workflows — not just built software for them.", color: "#0D9488" },
  { icon: "🔒", title: "HIPAA, DISHA & NABH Ready", desc: "Every healthcare product we build is compliant by default — end-to-end encryption, RBAC, audit logs, data residency controls, and compliance documentation included.", color: "#2563EB" },
  { icon: "🔗", title: "True Interoperability", desc: "HL7 FHIR R4, DICOM, ASTM, and ABDM/ABHA integration. We connect your system to government health registries, insurance networks, and lab instruments out of the box.", color: "#7C3AED" },
  { icon: "🤖", title: "AI That Actually Saves Lives", desc: "We don't add AI as a buzzword. Our diagnostic AI, clinical NLP, and risk prediction models are validated on real clinical data and monitored for bias and drift continuously.", color: "#DC2626" },
  { icon: "⚡", title: "Built for Mission-Critical Load", desc: "Healthcare systems cannot go down. We architect for 99.9%+ uptime — multi-AZ deployments, database replication, automated failover, and zero-downtime maintenance windows.", color: "#D97706" },
  { icon: "🌐", title: "Government Portal Integrations", desc: "ABHA (Ayushman Bharat), e-Sanjeevani, PMJAY insurance, DigiLocker, Co-WIN, and state-specific health registries — we've integrated them all and know the edge cases.", color: "#059669" },
];

const FAQS = [
  {
    q: "Is the healthcare software you build HIPAA and DISHA compliant?",
    a: "Yes — compliance is built into our development process, not retrofitted at the end. All healthcare products include AES-256 encryption at rest and in transit, role-based access control with least-privilege principles, full audit logging of every data access event, data residency controls (India-only or configurable), and a HIPAA risk assessment report. For Indian clients, we follow the DISHA (Digital Information Security in Healthcare Act) framework and NABH standards. We produce the compliance documentation your legal and audit teams need.",
  },
  {
    q: "Can you integrate with our existing HIS, lab instruments, and insurance systems?",
    a: "Absolutely. Healthcare interoperability is one of our core specialisations. We implement HL7 FHIR R4 APIs for EHR data exchange, DICOM for radiology imaging, ASTM for lab instrument interfaces, and dedicated connectors for major Indian insurance claim systems (PMJAY, TPA networks). We've also integrated with ABDM (Ayushman Bharat Digital Mission), ABHA health ID, e-Sanjeevani, and state-specific health registries. If you have a legacy HIS that doesn't support FHIR, we build custom ETL adapters.",
  },
  {
    q: "How long does it take to build a hospital management system or telemedicine platform?",
    a: "A focused telemedicine MVP (video consultation, appointment booking, e-prescription, basic patient records) takes 10–14 weeks. A comprehensive HMS covering OPD, IPD, pharmacy, lab, radiology, OT, billing, and a patient app typically takes 5–8 months. We deliver in phases — starting with the highest-value modules so your team sees ROI early. Every project starts with a 2-week clinical discovery sprint that produces a detailed scope, timeline, and fixed-price quote.",
  },
  {
    q: "Can your AI diagnostic tools integrate with our existing PACS/RIS?",
    a: "Yes. Our AI diagnostic modules connect to existing PACS (Picture Archiving and Communication Systems) and RIS (Radiology Information Systems) via DICOM WADO/STOW-RS standards. Radiologists continue working in their existing viewer — our AI analysis appears as an overlay or decision support panel. We support integration with major PACS vendors including Philips IntelliSpace, GE Centricity, Carestream, and Infinitt. Custom integration for other vendors is available.",
  },
  {
    q: "How do you handle data migration from our legacy system?",
    a: "We have a structured clinical data migration methodology: first we audit your existing data schema, identify data quality issues, and map legacy fields to FHIR-compliant structures. Migration happens in parallel runs with validation checks — we never do a single cutover without dry runs. Patient records, clinical notes, billing history, and lab results are migrated with full referential integrity verification. We typically run both systems in parallel for 4–8 weeks before full cutover.",
  },
  {
    q: "What post-launch support do you offer for healthcare systems?",
    a: "Healthcare systems require elevated support standards because clinical decisions depend on them. We offer three SLA tiers: Standard (business hours, 12hr response for critical issues), Clinical (extended hours, 4hr critical response), and Mission-Critical (24/7, 1hr critical response with on-call engineers). All tiers include proactive uptime monitoring, monthly security patch reviews, quarterly HIPAA audit log reviews, and a dedicated account manager who understands clinical workflows.",
  },
];

const STATS = [
  { val: "180+", lbl: "Healthcare Projects", icon: "🏥" },
  { val: "2M+", lbl: "Patients Served", icon: "👨‍⚕️" },
  { val: "99.9%", lbl: "Uptime Guarantee", icon: "⚡" },
  { val: "HIPAA", lbl: "Certified Compliance", icon: "🔒" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.93)",
      backdropFilter: "blur(20px)",
      borderBottom: scrolled ? "1px solid #CCFBF1" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 28px rgba(13,148,136,.08)" : "none",
      transition: "all .3s ease",
    }}>
      {/* Top bar */}
      <div style={{
        background: "linear-gradient(90deg,#042F2E,#0D9488,#0891B2)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .9 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>

      {/* Main bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>

        {/* Desktop links */}
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => (
            <div key={link.label} style={{ position: "relative" }}
              onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              {link.link ? (
                <a href={link.link} style={{
                  background: currentPath === link.link ? "#F0FDFA" : "none",
                  border: currentPath === link.link ? "1px solid #99F6E4" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: currentPath === link.link ? "#0D9488" : "#374151",
                  display: "flex", alignItems: "center", gap: 4,
                  fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none",
                  transition: "all .2s",
                }}>{link.label}</a>
              ) : (
                <button style={{
                  background: link.label === "Industries" ? "#F0FDFA" : "none",
                  border: link.label === "Industries" ? "1px solid #99F6E4" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: link.label === "Industries" ? "#0D9488" : activeDropdown === link.label ? "#0D9488" : "#374151",
                  display: "flex", alignItems: "center", gap: 4, transition: "all .2s",
                  fontFamily: "'Sora',sans-serif", borderRadius: 8,
                }}>
                  {link.label}
                  {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>}
                </button>
              )}
              {link.dropdown && activeDropdown === link.label && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, zIndex: 200,
                  background: "#fff", border: "1px solid #CCFBF1", borderRadius: 16,
                  boxShadow: "0 20px 60px rgba(13,148,136,.1)", minWidth: 230,
                  padding: "8px 0", animation: "fadeIn .2s ease",
                }}>
                  {link.dropdown.map(item => (
                    <a key={item.name} href={item.link} style={{
                      display: "block", padding: "10px 20px", fontSize: 13.5,
                      color: item.name === "Healthcare" ? "#0D9488" : "#374151",
                      background: item.name === "Healthcare" ? "#F0FDFA" : "none",
                      fontWeight: item.name === "Healthcare" ? 700 : 500,
                      textDecoration: "none", transition: "all .15s",
                      fontFamily: "'Sora',sans-serif",
                    }}
                      onMouseEnter={e => { e.target.style.background = "#F0FDFA"; e.target.style.color = "#0D9488"; }}
                      onMouseLeave={e => {
                        e.target.style.background = item.name === "Healthcare" ? "#F0FDFA" : "none";
                        e.target.style.color = item.name === "Healthcare" ? "#0D9488" : "#374151";
                      }}
                    >{item.name}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#042F2E,#0D9488)", color: "#fff",
            padding: "10px 22px", borderRadius: 10, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 16px rgba(13,148,136,.3)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(13,148,136,.42)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(13,148,136,.3)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: mobileOpen ? "#F0FDFA" : "none",
            border: "1px solid #CCFBF1", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
            transition: "all .2s",
          }}>{mobileOpen ? "✕" : "☰"}</button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{
          background: "#fff", borderTop: "1px solid #CCFBF1",
          maxHeight: "calc(100vh - 120px)", overflowY: "auto",
          boxShadow: "0 16px 48px rgba(0,0,0,.1)",
        }}>
          <div style={{ padding: "16px 24px 12px", borderBottom: "1px solid #F0FDFA" }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          <div style={{ padding: "8px 0" }}>
            {NAV_LINKS.map(link => (
              <div key={link.label}>
                {link.dropdown ? (
                  <>
                    <button onClick={() => setMobileDropdown(mobileDropdown === link.label ? null : link.label)} style={{
                      width: "100%", textAlign: "left", padding: "13px 24px",
                      background: mobileDropdown === link.label ? "#F0FDFA" : "none",
                      border: "none", borderBottom: "1px solid #F0FDFA",
                      color: mobileDropdown === link.label ? "#0D9488" : "#374151",
                      fontWeight: 600, fontSize: 15, fontFamily: "'Sora',sans-serif",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      {link.label}
                      <span style={{ fontSize: 11, color: "#9CA3AF", transition: "transform .2s", transform: mobileDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>
                    </button>
                    {mobileDropdown === link.label && (
                      <div style={{ background: "#F8FFFE", borderBottom: "1px solid #F0FDFA" }}>
                        {link.dropdown.map(item => (
                          <a key={item.name} href={item.link} onClick={() => setMobileOpen(false)} style={{
                            display: "block", padding: "11px 24px 11px 40px", fontSize: 14,
                            color: item.name === "Healthcare" ? "#0D9488" : "#4B5563",
                            background: item.name === "Healthcare" ? "#F0FDFA" : "none",
                            fontWeight: item.name === "Healthcare" ? 700 : 500,
                            textDecoration: "none", borderBottom: "1px solid #F0FDFA",
                            fontFamily: "'Sora',sans-serif",
                          }}>→ {item.name}</a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a href={link.link || "#"} onClick={() => setMobileOpen(false)} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "13px 24px",
                    color: currentPath === link.link ? "#0D9488" : "#374151",
                    background: currentPath === link.link ? "#F0FDFA" : "none",
                    fontWeight: 600, fontSize: 15, textDecoration: "none",
                    borderBottom: "1px solid #F0FDFA", fontFamily: "'Sora',sans-serif",
                  }}>
                    {link.label}
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>›</span>
                  </a>
                )}
              </div>
            ))}
          </div>
          <div style={{ padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <a href="#contact" onClick={() => setMobileOpen(false)} style={{
              background: "linear-gradient(135deg,#042F2E,#0D9488)", color: "#fff",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", boxShadow: "0 6px 20px rgba(13,148,136,.3)",
            }}>🏥 Get Free Consultation</a>
            <a href="tel:+919646206032" style={{
              background: "#F0FDFA", color: "#042F2E",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", border: "1px solid #99F6E4",
            }}>📞 Call: +91 96462 06032</a>
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [counter, setCounter] = useState(0);
  const metrics = ["HIPAA & DISHA Compliant", "99.9% Uptime Guarantee", "2M+ Patients Served", "180+ Healthcare Projects"];

  useEffect(() => {
    const t = setInterval(() => setCounter(c => (c + 1) % metrics.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 100,
      background: "linear-gradient(155deg,#F0FDFA 0%,#ECFEFF 35%,#F0F9FF 65%,#F8FAFC 100%)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Subtle cross-hatch grid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(13,148,136,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(13,148,136,.05) 1px,transparent 1px)",
        backgroundSize: "48px 48px",
      }} />

      {/* Curved organic blobs */}
      <div style={{ position: "absolute", top: -180, right: -120, width: 700, height: 700, borderRadius: "61% 39% 70% 30%/30% 50% 50% 70%", background: "radial-gradient(circle,rgba(13,148,136,.1),rgba(8,145,178,.05) 50%,transparent 75%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -120, left: -80, width: 560, height: 560, borderRadius: "39% 61% 30% 70%/70% 30% 70% 30%", background: "radial-gradient(circle,rgba(37,99,235,.07),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "30%", right: "30%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(124,58,237,.05),transparent 70%)", pointerEvents: "none" }} />

      {/* Decorative medical cross marks */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[{ x: "8%", y: "15%" }, { x: "92%", y: "20%" }, { x: "15%", y: "75%" }, { x: "85%", y: "70%" }, { x: "50%", y: "88%" }].map((pos, i) => (
          <div key={i} style={{
            position: "absolute", left: pos.x, top: pos.y,
            width: 20, height: 20,
            opacity: 0.12 + i * 0.03,
            animation: `hcPulse ${3 + i}s ease-in-out infinite ${i * 0.6}s`,
          }}>
            <div style={{ position: "absolute", top: "35%", left: 0, right: 0, height: "30%", background: "#0D9488", borderRadius: 2 }} />
            <div style={{ position: "absolute", left: "35%", top: 0, bottom: 0, width: "30%", background: "#0D9488", borderRadius: 2 }} />
          </div>
        ))}
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#9CA3AF" }}>
          <a href="/" style={{ color: "#0D9488", textDecoration: "none", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Home</a>
          <span>›</span>
          <span style={{ fontFamily: "'Sora',sans-serif" }}>Industries</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Healthcare</span>
        </div>

        <div className="hc-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#F0FDFA", border: "1px solid #99F6E4", borderRadius: 99, padding: "6px 18px", marginBottom: 28 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0D9488", display: "inline-block", animation: "hcPulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#042F2E", fontFamily: "'Sora',sans-serif" }}>🏥 India's Trusted Healthcare Technology Partner</span>
            </div>

            <h1 style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(36px,4.8vw,64px)",
              fontWeight: 900, color: "#042F2E",
              lineHeight: 1.1, marginBottom: 20,
            }}>
              Healthcare Technology<br />
              <span style={{ background: "linear-gradient(135deg,#0D9488,#0891B2,#2563EB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Built for Lives,
              </span><br />
              Not Just Systems.
            </h1>

            <p style={{ fontSize: 17, color: "#4B5563", lineHeight: 1.85, maxWidth: 570, marginBottom: 36, fontFamily: "'Sora',sans-serif", fontWeight: 400 }}>
              We build HIPAA & DISHA-compliant hospital management systems, telemedicine platforms, AI diagnostics, EHR systems, and patient engagement tools — engineered for the complexity of real clinical environments.
            </p>

            {/* Rotating metric pill */}
            <div style={{ background: "#F0FDFA", border: "1px solid #99F6E4", borderRadius: 12, padding: "13px 20px", marginBottom: 36, display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18 }}>✨</span>
              <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14.5, color: "#042F2E" }}>{metrics[counter]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 48 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#042F2E,#0D9488)", color: "#fff",
                padding: "15px 32px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 8px 28px rgba(13,148,136,.35)",
                transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(13,148,136,.48)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(13,148,136,.35)"; }}
              >🏥 Start Healthcare Project</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#042F2E",
                padding: "15px 30px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", border: "2px solid #99F6E4",
                transition: "all .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#F0FDFA"; e.currentTarget.style.borderColor = "#2DD4BF"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#99F6E4"; }}
              >📂 View Case Studies</a>
            </div>

            {/* Trust signal row */}
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "center" }}>
              {[["180+", "HC Projects"], ["2M+", "Patients Served"], ["99.9%", "Uptime SLA"], ["HIPAA", "Compliant"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 24, color: "#0D9488" }}>{v}</div>
                  <div style={{ fontSize: 11.5, color: "#9CA3AF", fontWeight: 600, marginTop: 2, fontFamily: "'Sora',sans-serif" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Visual panel */}
          <div style={{ position: "relative" }}>
            {/* Main image card */}
            <div style={{
              borderRadius: 28, overflow: "hidden",
              boxShadow: "0 40px 100px rgba(13,148,136,.14), 0 0 0 1px rgba(13,148,136,.08)",
              background: "#fff", position: "relative",
            }}>
              {/* Clinical dashboard chrome bar */}
              <div style={{ background: "#F8FFFE", borderBottom: "1px solid #CCFBF1", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {["#EF4444", "#F59E0B", "#10B981"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
                </div>
                <div style={{ flex: 1, background: "#F0FDFA", borderRadius: 8, padding: "5px 14px", fontSize: 12, color: "#0D9488", fontWeight: 600, margin: "0 12px", fontFamily: "'Sora',sans-serif" }}>
                  🔒 medicore-hms.techvanto.com
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, fontFamily: "'Sora',sans-serif", background: "#F0FDFA", padding: "4px 10px", borderRadius: 99, border: "1px solid #99F6E4" }}>LIVE</div>
              </div>
              <img
                src="https://images.unsplash.com/photo-1551076805-e1869033e561?w=640&h=380&fit=crop&auto=format"
                alt="Healthcare Dashboard"
                style={{ width: "100%", height: 340, objectFit: "cover", display: "block" }}
              />
              {/* Overlay gradient */}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 100, background: "linear-gradient(to top,rgba(4,47,46,.3),transparent)" }} />
            </div>

            {/* Floating badge — HIPAA */}
            <div style={{ position: "absolute", top: -18, left: -22, background: "#fff", borderRadius: 18, padding: "14px 18px", boxShadow: "0 12px 40px rgba(13,148,136,.18)", border: "1.5px solid #CCFBF1", animation: "float 3.2s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: "linear-gradient(135deg,#0D9488,#0891B2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🔒</div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 16, color: "#042F2E" }}>HIPAA</div>
                  <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>Certified Compliant</div>
                </div>
              </div>
            </div>

            {/* Floating badge — Uptime */}
            <div style={{ position: "absolute", bottom: 20, right: -22, background: "linear-gradient(135deg,#042F2E,#0D9488)", borderRadius: 18, padding: "14px 20px", boxShadow: "0 12px 36px rgba(13,148,136,.4)", animation: "float 3.8s ease-in-out infinite .6s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#fff" }}>99.9%</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>System Uptime ⚡</div>
            </div>

            {/* Floating badge — AI accuracy */}
            <div style={{ position: "absolute", bottom: -18, left: 16, background: "#fff", borderRadius: 16, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.09)", border: "1.5px solid #FEF3C7", animation: "float 4.2s ease-in-out infinite 1.2s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ fontSize: 20 }}>🤖</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#0F172A", fontFamily: "'Sora',sans-serif" }}>94.6% AI Accuracy</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>Diagnostic model</div>
                </div>
              </div>
            </div>

            {/* Floating badge — Patients */}
            <div style={{ position: "absolute", top: 56, right: -20, background: "#fff", borderRadius: 14, padding: "11px 15px", boxShadow: "0 6px 22px rgba(37,99,235,.12)", border: "1.5px solid #DBEAFE", animation: "float 4.6s ease-in-out infinite 1.8s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>👨‍⚕️</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#1E40AF", fontFamily: "'Sora',sans-serif" }}>2M+ Patients</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>Across India</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#042F2E", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }} className="hc-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>{s.icon}</div>
            <div style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(28px,3vw,44px)", fontWeight: 900, color: "#fff",
              lineHeight: 1,
            }}>{s.val}</div>
            <div style={{ color: "#5EEAD4", fontSize: 13, marginTop: 8, fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "100px 40px", background: "#fff" }} className="hc-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0D9488", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>What We Build</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#042F2E", marginTop: 10, marginBottom: 16 }}>Healthcare Software Solutions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 580, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>
            Clinical-grade software built for the real complexity of healthcare — from a single-doctor clinic to a 2,000-bed tertiary hospital network.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "34px 30px",
                border: `2px solid ${hov === i ? s.color : "#F0FDFA"}`,
                boxShadow: hov === i ? `0 28px 72px ${s.color}1E` : "0 2px 16px rgba(0,0,0,.04)",
                transition: "all .32s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-8px)" : "none",
                position: "relative", overflow: "hidden", cursor: "pointer",
              }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${s.color},${s.color}55)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${s.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${s.color}20` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 19, fontWeight: 800, color: "#042F2E", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, marginBottom: 20, fontFamily: "'Sora',sans-serif" }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}0E`, color: s.color, fontSize: 11, fontWeight: 700, padding: "4px 11px", borderRadius: 99, border: `1px solid ${s.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .22s", fontFamily: "'Sora',sans-serif" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#F8FFFE" }} className="hc-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0D9488", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#042F2E", marginTop: 10, marginBottom: 16 }}>Clinical Depth Meets Engineering Excellence</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 540, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>What separates a generic IT company from a true healthcare technology partner.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
          {WHY_US.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "32px 28px",
                border: `2px solid ${hov === i ? p.color : "#CCFBF1"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}16` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: 20,
              }}>
              <div style={{ width: 54, height: 54, borderRadius: 16, background: `${p.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1.5px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#042F2E", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, fontFamily: "'Sora',sans-serif" }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "100px 40px", background: "#fff" }} className="hc-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#2563EB", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Technology</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#042F2E", marginTop: 10, marginBottom: 16 }}>Clinical-Grade Technology Stack</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>The frameworks, tools, and platforms powering our healthcare solutions — chosen for reliability, not novelty.</p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#042F2E,#0D9488)" : "#fff",
              borderColor: active === i ? "#0D9488" : "#CCFBF1",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(13,148,136,.28)" : "none",
            }}>{t.category}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 22,
                border: `2px solid ${hov === i ? tech.color : "#F0FDFA"}`,
                background: hov === i ? `${tech.color}07` : "#FAFFFE",
                boxShadow: hov === i ? `0 14px 42px ${tech.color}22` : "0 1px 6px rgba(0,0,0,.04)",
                transition: "all .3s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-7px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}14` : "#fff", border: `1px solid ${hov === i ? tech.color + "28" : "#CCFBF1"}`, boxShadow: hov === i ? `0 4px 18px ${tech.color}20` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .3s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .3s", transform: hov === i ? "scale(1.12)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 3, fontFamily: "'Sora',sans-serif" }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 20px 20px", background: `linear-gradient(90deg,${tech.color},${tech.color}60)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "100px 40px", background: "linear-gradient(155deg,#F0FDFA 0%,#ECFEFF 50%,#EFF6FF 100%)" }} className="hc-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 76 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#042F2E", marginTop: 10, marginBottom: 16 }}>How We Build Healthcare Software</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>A clinical-grade development process — designed around patient safety, regulatory compliance, and real-world clinical workflows.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 24, padding: "34px 30px",
              border: "1.5px solid #CCFBF1",
              boxShadow: "0 4px 20px rgba(13,148,136,.06)",
              position: "relative", overflow: "hidden",
              transition: "all .32s ease",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = step.color; e.currentTarget.style.boxShadow = `0 20px 56px ${step.color}16`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#CCFBF1"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(13,148,136,.06)"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}10`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${step.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${step.color}22` }}>
                {step.icon}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}12`, padding: "3px 12px", borderRadius: 99, display: "inline-block", marginBottom: 12, letterSpacing: ".04em", fontFamily: "'Sora',sans-serif" }}>STEP {step.n}</span>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#042F2E", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}44)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "100px 40px", background: "#fff" }} className="hc-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#DC2626", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#042F2E", marginTop: 10, marginBottom: 16 }}>Healthcare Systems We're Proud Of</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>Production systems serving real clinicians and real patients — with numbers that matter in healthcare.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 68 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 56, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="hc-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 24, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}1E` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 380, objectFit: "cover", display: "block", transition: "transform .5s ease", transform: hov === cs.title ? "scale(1.05)" : "scale(1)" }} />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${cs.color}44,transparent 60%)` }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 16px", borderRadius: 99, fontFamily: "'Sora',sans-serif" }}>{cs.industry}</div>
              </div>

              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,40px)", fontWeight: 900, color: "#042F2E", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.85, marginBottom: 26, fontFamily: "'Sora',sans-serif" }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 30 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}0E`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "5px 13px", borderRadius: 99, border: `1px solid ${cs.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 32, marginBottom: 36, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 30, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 5, fontFamily: "'Sora',sans-serif" }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}30`,
                  transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 10px 28px ${cs.color}44`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 6px 20px ${cs.color}30`; }}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 68 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#0D9488", border: "2px solid #99F6E4",
            padding: "14px 36px", borderRadius: 12, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .22s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F0FDFA"; e.currentTarget.style.borderColor = "#2DD4BF"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#99F6E4"; }}
          >View All Healthcare Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#F8FFFE" }} className="hc-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#4F46E5", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#042F2E", marginTop: 10, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.7, fontFamily: "'Sora',sans-serif" }}>Everything you need to know before starting your healthcare technology project.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 18,
              border: `2px solid ${open === i ? "#0D9488" : "#CCFBF1"}`,
              overflow: "hidden", transition: "border-color .25s, box-shadow .25s",
              boxShadow: open === i ? "0 8px 36px rgba(13,148,136,.1)" : "0 1px 4px rgba(0,0,0,.04)",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 30px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 600, fontSize: 15.5, color: "#042F2E", textAlign: "left", lineHeight: 1.45, flex: 1, marginRight: 18 }}>{faq.q}</span>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: open === i ? "linear-gradient(135deg,#042F2E,#0D9488)" : "#F0FDFA",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .28s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#0D9488", fontSize: 19, lineHeight: 1, display: "block", transform: open === i ? "rotate(45deg)" : "none", transition: "transform .28s" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 30px 26px", animation: "fadeIn .22s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.85, borderTop: "1px solid #F0FDFA", paddingTop: 18, fontFamily: "'Sora',sans-serif" }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function HealthcareSolution() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float   { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:.4; } }
        @keyframes hcPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.45; transform:scale(0.96); } }

        /* ── Desktop base ── */
        .nav-links       { display:flex; align-items:center; gap:4px; }
        .nav-cta         { display:flex !important; }
        .mob-menu-btn    { display:none; }

        .hc-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .hc-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; text-align:center; }
        .hc-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .footer-grid     { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }

        /* ── ≤1100px ── */
        @media (max-width:1100px) {
          .hc-hero-grid  { grid-template-columns:1fr 1fr; gap:40px; }
          .footer-grid   { grid-template-columns:1fr 1fr; gap:32px; }
          .hc-stats-grid { grid-template-columns:repeat(2,1fr); }
        }

        /* ── ≤900px ── */
        @media (max-width:900px) {
          .hc-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .hc-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .footer-grid     { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links       { display:none !important; }
          .nav-cta         { display:none !important; }
          .mob-menu-btn    { display:flex !important; }
          .hc-section      { padding-left:20px !important; padding-right:20px !important; }
        }

        /* ── ≤640px ── */
        @media (max-width:640px) {
          .footer-grid     { grid-template-columns:1fr; }
          .hc-stats-grid   { grid-template-columns:repeat(2,1fr); gap:24px; }
          .hc-section      { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; }
          section          { padding-left:16px !important; padding-right:16px !important; }
          footer           { padding-left:16px !important; padding-right:16px !important; }
        }

        /* ── ≤480px ── */
        @media (max-width:480px) {
          .footer-grid     { grid-template-columns:1fr !important; gap:28px; }
          .hc-stats-grid   { grid-template-columns:1fr 1fr; gap:20px; }
        }

        /* ── ≤500px ── */
        @media (max-width:500px) {
          .topbar-right    { display:none !important; }
        }

        /* ── Navbar padding on mobile ── */
        @media (max-width:640px) {
          nav > div:nth-child(2) { padding: 0 16px !important; }
          nav > div:first-child  { padding: 6px 16px !important; }
        }

        /* ── Hero floating badges — prevent overflow on mobile ── */
        @media (max-width:640px) {
          .hc-hero-grid > div:last-child { margin-top: 8px; }
        }
      `}</style>

      <Navbar />
      <Hero />
      <StatsBar />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}